import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { SubjectSpinePositionGraph } from '../components/learning/SubjectSpinePositionGraph';
import { useAuth } from '../context/AuthContext';
import {
  reviewService,
  type LearningPositionSpineGraph,
  type LearningPositionSubjectSummary,
} from '../services/reviewService';
import {
  getCachedLearningPositionSpineGraph,
  invalidateLearningPositionSpineGraphCache,
  setCachedLearningPositionSpineGraph,
} from '../utils/learningPositionCache';
import { SUBJECT_CONFIG, normalizeSubjectCode } from '../utils/subjectConfig';

export function LearningPositionPage() {
  const { user } = useAuth();
  const userId = user?.id || '';

  const [subjects, setSubjects] = useState<LearningPositionSubjectSummary[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [graph, setGraph] = useState<LearningPositionSpineGraph | null>(null);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSubjects = useCallback(async () => {
    if (!userId) return;
    try {
      setLoadingSubjects(true);
      setError(null);
      const data = await reviewService.loadLearningPositionSubjects(userId);
      setSubjects(data);
      setSelectedSubject((current) => current || data[0]?.subject || null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载学情学科失败');
    } finally {
      setLoadingSubjects(false);
    }
  }, [userId]);

  const loadGraph = useCallback(async (subject: string, options?: { force?: boolean }) => {
    if (!userId) return;
    const force = options?.force ?? false;

    if (!force) {
      const cached = getCachedLearningPositionSpineGraph(userId, subject);
      if (cached) {
        setGraph(cached);
        setLoadingGraph(false);
        setError(null);
        return;
      }
    }

    try {
      setLoadingGraph(true);
      setError(null);
      const data = await reviewService.loadLearningPositionSpineGraph(userId, subject);
      setCachedLearningPositionSpineGraph(userId, subject, data);
      setGraph(data);
    } catch (loadError) {
      setGraph(null);
      setError(loadError instanceof Error ? loadError.message : '加载学科主干学情失败');
    } finally {
      setLoadingGraph(false);
    }
  }, [userId]);

  const refreshVisibleData = useCallback(async () => {
    if (!userId) return;
    invalidateLearningPositionSpineGraphCache(userId);
    await loadSubjects();
    if (selectedSubject) {
      await loadGraph(selectedSubject, { force: true });
    }
  }, [loadGraph, loadSubjects, selectedSubject, userId]);

  useEffect(() => {
    if (!userId) return;
    invalidateLearningPositionSpineGraphCache(userId);
    void loadSubjects();
  }, [loadSubjects, userId]);

  useEffect(() => {
    if (!selectedSubject) return;
    void loadGraph(selectedSubject, { force: true });
  }, [loadGraph, selectedSubject]);

  const activeSubjectSummary = useMemo(
    () => subjects.find((entry) => entry.subject === selectedSubject) ?? null,
    [selectedSubject, subjects],
  );

  if (!userId) return null;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-ink">学情位置</h1>
        <p className="mt-1 text-sm text-muted">将历史刷卡轨迹映射到学科主干节点上。</p>
      </div>

      {loadingSubjects ? (
        <div className="flex flex-col items-center gap-3 py-16">
          <Loader2 className="animate-spin text-primary" size={32} />
          <p className="text-sm text-muted">正在加载学情位置…</p>
        </div>
      ) : error && !graph ? (
        <div className="rounded-xl border border-border bg-surface p-5 text-center">
          <p className="text-danger">{error}</p>
          <button
            type="button"
            onClick={() => void refreshVisibleData()}
            className="mt-4 rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary"
          >
            重试
          </button>
        </div>
      ) : !subjects.length ? (
        <div className="rounded-xl border border-border bg-surface p-5 text-center">
          <h2 className="font-semibold text-ink">还没有可映射的历史轨迹</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted">
            先在 Stream 中刷过卡族并产生历史足迹，这里会把轨迹映射到学科画像主干上。
          </p>
        </div>
      ) : (
        <>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {subjects.map((entry) => {
              const subjectCode = normalizeSubjectCode(entry.subject);
              const config = SUBJECT_CONFIG[subjectCode];
              const isActive = selectedSubject === entry.subject;

              return (
                <button
                  key={entry.subject}
                  type="button"
                  onClick={() => setSelectedSubject(entry.subject)}
                  className={`flex shrink-0 items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                    isActive
                      ? 'border-primary bg-primary/10 font-semibold text-primary'
                      : 'border-border bg-surface text-ink hover:bg-canvas'
                  }`}
                >
                  <span
                    className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white"
                    style={{ backgroundColor: config.color }}
                  >
                    {config.char}
                  </span>
                  {config.label}
                </button>
              );
            })}
          </div>

          {activeSubjectSummary ? (
            <div className="rounded-xl border border-border bg-canvas px-4 py-3 text-sm text-muted">
              {SUBJECT_CONFIG[normalizeSubjectCode(activeSubjectSummary.subject)].label} ·{' '}
              {activeSubjectSummary.footprintCount} 次历史轨迹
            </div>
          ) : null}

          {loadingGraph ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="animate-spin text-primary" size={28} />
              <p className="text-sm text-muted">正在映射学科主干…</p>
            </div>
          ) : graph ? (
            <SubjectSpinePositionGraph graph={graph} />
          ) : null}
        </>
      )}
    </div>
  );
}
